// Named places in the apps where modules put their screens (docs/spec/modules.md §2, PMU-181).
// The apps build their menus from their own core entries plus whatever the installed modules
// registered here; a module's entry says where it goes and which roles may see it. The apps
// register their modules once at startup (registerUiModules), so this package never imports one.
import { useEffect, useReducer, type ReactNode } from "react";
import type { IconName } from "@vavr/ui";
import type { Permission } from "@vavr/core";

export interface SlotEntries {
  /** A tab in the staff app, between the core tabs (placed after `after`, default before Me). */
  /** `permission`: the tab shows only to staff who hold it at this site (the Bus tab: transport:ride). */
  /** `count`: the number on the tab (e.g. unread messages), a store the app subscribes to. */
  /** `topBar`: the tab's own buttons in the app's top bar (e.g. Messages' settings). */
  "staff.tab": { id: string; label: string; icon: IconName; path: string; title: string; after?: string; permission?: string; count?: TabCount; topBar?: () => ReactNode; render: () => ReactNode }[];
  /** A staff app page at `/teacher/<path>` (":name" parts become params and sub-paths belong to it), under the given tab, with a back arrow; reached from a module's bar or card. */
  "staff.page": { id: string; path: string; title: string; tab?: string; permission?: string; /** The whole screen: no top bar, tab bar or footers (the page brings its own way back). */ full?: boolean; render: () => ReactNode }[];
  /** Wraps the whole staff app (inside its context), e.g. an engine that must live across tabs. */
  "staff.wrap": { id: string; wrap: (children: ReactNode) => ReactNode }[];
  /** Wraps the whole door kiosk (inside its state), e.g. the emergency screen over everything. */
  "kiosk.wrap": { id: string; wrap: (children: ReactNode) => ReactNode }[];
  /** Under the page in the staff app, on the listed tabs (all tabs when omitted). */
  "staff.footer": { id: string; tabs?: string[]; permission?: string; render: () => ReactNode }[];
  /** A section of the operations console: a nav entry and its page. */
  /** `orgKind`: only for that kind of organisation (schools or bus companies); both when unset. */
  /** `group`: the sidebar heading it sits under (Daily, People, Operations, Transport, Administration); Operations when unset. */
  "console.nav": { id: string; label: string; icon: IconName; path: string; title: string; key?: string; permission?: Permission; orgKind?: "school" | "operator"; group?: string; /** The page fills the space under the top bar, edge to edge (Messages). */ fullBleed?: boolean; render: () => ReactNode }[];
  /**
   * Results a module adds to the console's global search: `path` is fetched (cached) while search
   * is open, and `items` turns the response into results, each opening `href`.
   */
  "console.search": { id: string; label: string; permission?: Permission; orgKind?: "school" | "operator"; path: (site: { id: string; org_id: string }) => string; items: (data: any) => SearchHit[] }[];
  /** Floating over every console page (e.g. the radio button), for users with `permission`. */
  "console.overlay": { id: string; permission?: Permission; render: (c: { siteId: string }) => ReactNode }[];
  /** A tab in the parent app's bar, after `after` (default: after Home), with its page at `/guardian/<path>`. */
  "guardian.tab": { id: string; label: string; icon: IconName; path: string; title: string; after?: string; count?: TabCount; topBar?: () => ReactNode; render: () => ReactNode }[];
  /** Wraps the whole parent app (inside its context), e.g. a module's state shared across tabs. */
  "guardian.wrap": { id: string; wrap: (children: ReactNode) => ReactNode }[];
  /** A card at the top of the parent app's Messages page (e.g. Lost and found). */
  "guardian.inbox": { id: string; render: () => ReactNode }[];
  /** A card on the parent app's home screen. */
  "guardian.home": { id: string; render: () => ReactNode }[];
  /**
   * A page of the parent app at `/guardian/<path>` (":name" parts become params), under the given
   * tab, with a back arrow; e.g. the bus's full view.
   */
  "guardian.page": { id: string; path: string; title: string; tab?: string; render: (params: Record<string, string>) => ReactNode }[];
  /**
   * A page anyone can open without signing in, at `/<path>` (":name" parts become params), e.g. a
   * school's public calendar. `embed`: shown inside another website, so the web app adds no frame.
   */
  "public.page": { id: string; path: string; title: string; embed?: boolean; render: (params: Record<string, string>) => ReactNode }[];
}
export type SlotName = keyof SlotEntries;

/** One global search result: matched against `label` and `detail`. */
export interface SearchHit { id: string; label: string; detail?: string; icon?: IconName; href: string }

/** A number a module shows on its tab; the app re-renders when it changes. */
export interface TabCount { get(): number; subscribe(fn: () => void): () => void }

/** The counts of these tabs, kept current (one hook, whatever the tabs). */
export function useTabCounts(tabs: { id: string; count?: TabCount }[]): Record<string, number> {
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const key = tabs.map((t) => t.id).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const offs = tabs.map((t) => t.count?.subscribe(bump)); return () => offs.forEach((o) => o?.()); }, [key]);
  return Object.fromEntries(tabs.map((t) => [t.id, t.count?.get() ?? 0]));
}

/** A module's screens, by slot. */
export interface UiPart {
  id: string;
  slots: { [K in SlotName]?: SlotEntries[K] };
}

const parts: UiPart[] = [];
/** Modules switched off for the current organisation (PMU-185); their slots render nothing. */
let isEnabled: (moduleId: string) => boolean = () => true;

/** Called once by each app at startup with the modules it ships. Registering twice is harmless. */
export function registerUiModules(list: readonly UiPart[]) {
  for (const p of list) if (!parts.some((x) => x.id === p.id)) parts.push(p);
}

/** Which modules the signed-in organisation has; set by the app once it knows (PMU-185). */
export function setEnabledModules(check: (moduleId: string) => boolean) { isEnabled = check; }

/** Every entry registered for `name` by enabled modules, in registration order. */
export function slot<K extends SlotName>(name: K): SlotEntries[K] {
  return parts.filter((p) => isEnabled(p.id)).flatMap((p) => (p.slots[name] ?? []) as SlotEntries[K][number][]) as SlotEntries[K];
}

/** Test support: forget registered modules. */
export function resetUiModules() { parts.length = 0; isEnabled = () => true; }
